import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React from 'react';

/**
 * The Maestro launch artwork: a label-free node graph laid on a plane that
 * tilts away from the viewer, with out-of-focus shapes in front of and behind
 * it for depth. Drawn in CSS rather than shipped as an asset so the grid and
 * the glow can follow the theme.
 *
 * The violet ramp below is deliberately raw: it is the decorative gradient
 * from the launch design and Apollo has no token for it (`ColorChartPurple*`
 * is magenta-leaning and carries chart semantics). Everything that is not
 * decoration — the dot grid, the surrounding text, the glow — uses tokens.
 */
const Ramp = {
    violet300: '#c4b5fd',
    violet400: '#a78bfa',
    violet500: '#8b5cf6',
    purple300: '#d8b4fe',
    purple400: '#c084fc',
    purple500: '#a855f7',
    fuchsia400: '#e879f9',
    fuchsia500: '#d946ef',
    indigo400: '#818cf8',
    indigo500: '#6366f1',
    /** Icons sit on the gradient tiles, which stay bright in both themes, so
     * this is fixed rather than a theme token. */
    onTile: '#ffffff',
};

/** Node size at `depth: 1`, in px. */
const NodeSize = 52;
/** Half a node's width/height in the plane's 0-100 coordinate space. The plane
 * is wider than it is tall, so the two differ. */
const NodeRadiusX = 7;
const NodeRadiusY = 9.1;

const strokeIcon = (size: number, paths: React.ReactNode) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true">
        {paths}
    </svg>
);

const ZapIcon = (size: number) => strokeIcon(size, (
    // eslint-disable-next-line max-len
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
));

const BotIcon = (size: number) => strokeIcon(size, (
    <>
        <path d="M12 8V4H8" />
        <rect
            width="16"
            height="12"
            x="4"
            y="8"
            rx="2" />
        <path d="M2 14h2" />
        <path d="M20 14h2" />
        <path d="M15 13v2" />
        <path d="M9 13v2" />
    </>
));

const GitBranchIcon = (size: number) => strokeIcon(size, (
    <>
        <path d="M15 6a9 9 0 0 0-9 9V3" />
        <circle
            cx="18"
            cy="6"
            r="3" />
        <circle
            cx="6"
            cy="18"
            r="3" />
    </>
));

const CloudIcon = (size: number) => strokeIcon(size, (
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
));

const UsersIcon = (size: number) => strokeIcon(size, (
    <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <path d="M16 3.128a4 4 0 0 1 0 7.744" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <circle
            cx="9"
            cy="7"
            r="4" />
    </>
));

const CpuIcon = (size: number) => strokeIcon(size, (
    <>
        <path d="M12 20v2M12 2v2M17 20v2M17 2v2M7 20v2M7 2v2" />
        <path d="M2 12h2M2 17h2M2 7h2M20 12h2M20 17h2M20 7h2" />
        <rect
            x="4"
            y="4"
            width="16"
            height="16"
            rx="2" />
        <rect
            x="8"
            y="8"
            width="8"
            height="8"
            rx="1" />
    </>
));

interface GraphTile {
    id: string;
    /** Position on the plane, in the same 0-100 space as the edge viewBox. */
    x: number;
    y: number;
    from: string;
    to: string;
    icon?: (size: number) => React.ReactNode;
    /** Triggers are round; every other node is a squircle. */
    trigger?: boolean;
    /** Below 1 pushes the tile back: smaller, dimmer, out of focus. */
    depth?: number;
}

const GraphTiles: GraphTile[] = [
    {
        id: 'trigger',
        x: 8,
        y: 30,
        icon: ZapIcon,
        from: Ramp.violet500,
        to: Ramp.purple400,
        trigger: true,
    },
    {
        id: 'agent',
        x: 32,
        y: 30,
        icon: BotIcon,
        from: Ramp.fuchsia500,
        to: Ramp.violet400,
    },
    {
        id: 'branch',
        x: 56,
        y: 30,
        icon: GitBranchIcon,
        from: Ramp.purple500,
        to: Ramp.indigo400,
    },
    {
        id: 'api',
        x: 80,
        y: 30,
        icon: CloudIcon,
        from: Ramp.violet400,
        to: Ramp.purple300,
    },
    {
        id: 'people',
        x: 56,
        y: 66,
        icon: UsersIcon,
        from: Ramp.indigo500,
        to: Ramp.violet400,
    },
    {
        id: 'robot',
        x: 80,
        y: 66,
        icon: CpuIcon,
        from: Ramp.purple500,
        to: Ramp.fuchsia400,
    },
];

const GraphEdges: Array<[ string, string ]> = [
    [ 'trigger', 'agent' ],
    [ 'agent', 'branch' ],
    [ 'branch', 'api' ],
    [ 'branch', 'people' ],
    [ 'people', 'robot' ],
];

/** Tiles that sit off the plane purely for depth: two far behind it, two close
 * to the viewer and out of focus. */
const DepthTiles: GraphTile[] = [
    {
        id: 'far-low',
        x: 14,
        y: 82,
        from: Ramp.violet500,
        to: Ramp.purple400,
        depth: 0.55,
    },
    {
        id: 'far-high',
        x: 90,
        y: 12,
        from: Ramp.fuchsia500,
        to: Ramp.violet400,
        trigger: true,
        depth: 0.45,
    },
];

/**
 * An edge leaves its source's right handle and enters its target's left handle
 * with horizontal tangents at both ends, the way the Flow canvas routes. A
 * target sitting (almost) straight below its source takes the bottom handle
 * instead — a side handle there would loop the line back on itself.
 */
const edgePath = (a: GraphTile, b: GraphTile) => {
    if (Math.abs(b.x - a.x) < NodeRadiusX * 2 && b.y > a.y) {
        const startY = a.y + NodeRadiusY;
        const endY = b.y - NodeRadiusY;
        const midY = (startY + endY) / 2;
        return `M ${a.x} ${startY} C ${a.x} ${midY}, ${b.x} ${midY}, ${b.x} ${endY}`;
    }
    const startX = a.x + NodeRadiusX;
    const endX = b.x - NodeRadiusX;
    const bend = Math.max(4, (endX - startX) / 2);
    return `M ${startX} ${a.y} C ${startX + bend} ${a.y}, ${endX - bend} ${b.y}, ${endX} ${b.y}`;
};

const dotGrid = (color: string) => `radial-gradient(circle, ${color} 1px, transparent 1px)`;
const gridFade = 'radial-gradient(ellipse at center, black 35%, transparent 75%)';

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: '236px',
            perspective: '900px',
            pointerEvents: 'none',
        },
        plane: {
            position: 'absolute',
            left: '4%',
            right: '4%',
            top: '10%',
            bottom: '10%',
            transformStyle: 'preserve-3d',
            transform: 'rotateX(28deg) rotateY(-16deg) rotateZ(5deg)',
        },
        grid: {
            position: 'absolute',
            left: '-12%',
            right: '-12%',
            top: '-12%',
            bottom: '-12%',
            opacity: 0.4,
            backgroundImage: dotGrid(theme.palette.semantic.colorForegroundDeEmp),
            backgroundSize: '16px 16px',
            WebkitMaskImage: gridFade,
            maskImage: gridFade,
        },
        edges: {
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            color: Ramp.violet300,
        },
        edgeHalo: { opacity: 0.25 },
        tile: {
            position: 'absolute',
            display: 'grid',
            placeItems: 'center',
            transform: 'translate(-50%, -50%)',
            color: Ramp.onTile,
            boxShadow: `0 14px 28px -10px ${Ramp.violet500}`,
        },
        blob: {
            position: 'absolute',
            borderRadius: '30%',
            opacity: 0.7,
            filter: 'blur(3px)',
        },
        blobNear: {
            left: '6%',
            top: '10%',
            width: '64px',
            height: '64px',
            background: `linear-gradient(135deg, ${Ramp.violet500}, ${Ramp.purple400})`,
        },
        blobFront: {
            left: '70%',
            top: '86%',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            opacity: 0.6,
            filter: 'blur(2px)',
            background: `linear-gradient(135deg, ${Ramp.fuchsia400}, ${Ramp.violet400})`,
        },
    }),
);

const Tile = ({ tile }: { tile: GraphTile }) => {
    const classes = useStyles();
    const depth = tile.depth ?? 1;
    const size = NodeSize * depth;
    const back = depth < 1;

    return (
        <span
            className={classes.tile}
            style={{
                left: `${tile.x}%`,
                top: `${tile.y}%`,
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: tile.trigger ? '50%' : '30%',
                background: `linear-gradient(135deg, ${tile.from}, ${tile.to})`,
                opacity: back ? 0.55 + (depth * 0.3) : 1,
                filter: back ? `blur(${(1 - depth) * 3}px)` : undefined,
            }}>
            {tile.icon?.(Math.round(20 * depth))}
        </span>
    );
};

const MaestroFlowIllustration = ({
    className, ...rest
}: React.HTMLAttributes<HTMLDivElement>) => {
    const classes = useStyles();
    const tilesById = Object.fromEntries(GraphTiles.map((tile) => [ tile.id, tile ]));

    return (
        <div
            className={className ? `${classes.root} ${className}` : classes.root}
            aria-hidden="true"
            {...rest}>
            {DepthTiles.map((tile) => (
                <Tile
                    key={tile.id}
                    tile={tile} />
            ))}

            <div className={classes.plane}>
                <div className={classes.grid} />

                <svg
                    className={classes.edges}
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round">
                    {GraphEdges.map(([ from, to ]) => {
                        const d = edgePath(tilesById[from], tilesById[to]);
                        return (
                            <g key={`${from}-${to}`}>
                                <path
                                    d={d}
                                    strokeWidth="6"
                                    className={classes.edgeHalo}
                                    vectorEffect="non-scaling-stroke" />
                                <path
                                    d={d}
                                    strokeWidth="1.5"
                                    vectorEffect="non-scaling-stroke" />
                            </g>
                        );
                    })}
                </svg>

                {GraphTiles.map((tile) => (
                    <Tile
                        key={tile.id}
                        tile={tile} />
                ))}
            </div>

            <span className={`${classes.blob} ${classes.blobNear}`} />
            <span className={`${classes.blob} ${classes.blobFront}`} />
        </div>
    );
};

export default MaestroFlowIllustration;
